import React, { useState, useEffect } from 'react';
import { getDashboardCharts } from 'api/dashboard';

// Vintage Gold Texture Filter
const VintageGoldDefs = () => (
    <defs>
        {/* Base Gold Gradient */}
        <linearGradient id="vintageGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#bf9b68" />
            <stop offset="30%" stopColor="#8c6a3e" />
            <stop offset="50%" stopColor="#e3c48b" />
            <stop offset="70%" stopColor="#8c6a3e" />
            <stop offset="100%" stopColor="#bf9b68" />
        </linearGradient>

        {/* Dark Metal Center Gradient */}
        <radialGradient id="darkMetalGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4a3525" />
            <stop offset="80%" stopColor="#2e2118" />
            <stop offset="100%" stopColor="#1a120d" />
        </radialGradient>

        {/* Noise Texture for Gold */}
        <filter id="goldTexture" x="-20%" y="-20%" width="140%" height="140%">
            <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" result="noise" />
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.4 0" in="noise" result="coloredNoise" />
            <feComposite operator="in" in="coloredNoise" in2="SourceGraphic" result="texturedSource" />
            <feBlend mode="multiply" in="texturedSource" in2="SourceGraphic" result="blended" />
            
            {/* Bevel/Emboss Effect */}
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
            <feSpecularLighting in="blur" surfaceScale="3" specularConstant="1" specularExponent="15" lightingColor="#fff8e7" result="specOut">
                <fePointLight x="-5000" y="-10000" z="10000" />
            </feSpecularLighting>
            <feComposite in="specOut" in2="SourceAlpha" operator="in" result="specOut" />
            <feComposite in="SourceGraphic" in2="specOut" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litPaint" />
            <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.5" />
        </filter>

        {/* Text Emboss Filter */}
        <filter id="textEmboss">
            <feDropShadow dx="1" dy="1" stdDeviation="0.5" floodColor="rgba(0,0,0,0.8)" />
        </filter>
        
        {/* Inner Shadow for Center Circle */}
        <filter id="centerInnerShadow">
             <feComponentTransfer in="SourceAlpha">
                <feFuncA type="table" tableValues="1 0" />
            </feComponentTransfer>
            <feGaussianBlur stdDeviation="3"/>
            <feOffset dx="0" dy="2" result="shadow"/>
            <feComposite in="shadow" in2="SourceAlpha" operator="in" result="shadow" />
            <feComposite in="shadow" in2="SourceGraphic" operator="over"/>
        </filter>
    </defs>
);

const Segment = ({ startAngle, endAngle, label }) => {
    // Convert polar to cartesian
    const radius = 75; // Slightly smaller to fit stroke
    const center = 100;
    const strokeWidth = 40;
    
    // Adjust angles for gaps - extremely thin gaps for "cut" look
    const gap = 0.8; 
    const start = startAngle + gap;
    const end = endAngle - gap;

    // Helper to calculate point
    const getPoint = (angle) => {
        const rad = Math.PI * (angle - 90) / 180; // -90 to rotate so 0 is top
        return {
            x: center + radius * Math.cos(rad),
            y: center + radius * Math.sin(rad)
        };
    };
    
    // Label position (midpoint of segment)
    const midAngle = (start + end) / 2;
    // Position for the label text (radius + a bit)
    // Actually label is ON the segment, so radius 75 is center of stroke
    const labelRad = Math.PI * (midAngle - 90) / 180;
    const labelX = center + 75 * Math.cos(labelRad);
    const labelY = center + 75 * Math.sin(labelRad);

    const p1 = getPoint(start);
    const p2 = getPoint(end);

    const d = [
        `M ${p1.x} ${p1.y}`,
        `A ${radius} ${radius} 0 0 1 ${p2.x} ${p2.y}`
    ].join(" ");

    return (
        <g>
            <path 
                d={d} 
                fill="none" 
                stroke="url(#vintageGoldGradient)" 
                strokeWidth={strokeWidth}
                strokeLinecap="butt"
                filter="url(#goldTexture)"
            />
            {/* Segment Percentage Label */}
            <text 
                x={labelX} 
                y={labelY} 
                textAnchor="middle" 
                dominantBaseline="middle" 
                fill="#3e2b26" 
                fontFamily="Cinzel" 
                fontSize="11" 
                fontWeight="800"
                style={{ pointerEvents: 'none' }}
            >
                {label}
            </text>
        </g>
    );
};

const BhimaGoldChart = ({ title, value, subtext, labels = {}, apiParams }) => {
    const [chartData, setChartData] = useState({
        value,
        subtext,
        labels
    });

    useEffect(() => {
        if (apiParams) {
            getDashboardCharts(apiParams)
                .then(data => {
                    if (data) {
                        // Handle if response is array (take first) or object
                        const result = Array.isArray(data) ? data[0] : data;
                        if (result) {
                            let newValue = result.value;
                            
                            // Handle updated_df_data from API
                            if (result.updated_df_data && Array.isArray(result.updated_df_data) && result.updated_df_data.length > 0) {
                                const firstRow = result.updated_df_data[0];
                                const values = Object.values(firstRow);
                                if (values.length > 0) {
                                    newValue = values[0];
                                }
                            }

                            setChartData(prev => ({
                                ...prev,
                                value: newValue !== undefined ? newValue : prev.value,
                                subtext: result.subtext || prev.subtext,
                                labels: {
                                    ...prev.labels,
                                    ...(result.labels || {})
                                }
                            }));
                        }
                    }
                })
                .catch(err => {
                    console.error("Failed to fetch BhimaGoldChart data:", err);
                });
        }
    }, [apiParams]);

    const { left = "45%", right = "35%", bottomRight } = chartData.labels || {};
    const displayValue = chartData.value;
    const displaySubtext = chartData.subtext;

    return (
        <div className="bhima-card" style={{ justifyContent: 'space-between', paddingBottom: '15px' }}>
            <div className="bhima-card-title" style={{ minHeight: 'auto', marginBottom: '5px', padding: '0 10px' }}>
                {title}
            </div>

            <div style={{ position: 'relative', width: '240px', height: '200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                
                {/* Outer Labels - Positioned absolutely relative to the container */}
                {/* Left Label (45%) */}
                <div style={{ 
                    position: 'absolute', 
                    top: '40px', 
                    left: '10px', 
                    fontFamily: 'Cinzel', 
                    fontWeight: '700', 
                    fontSize: '16px',
                    color: '#3e2b26' 
                }}>
                    {left}
                </div>

                {/* Right Label (35%) */}
                <div style={{ 
                    position: 'absolute', 
                    top: '40px', 
                    right: '10px', 
                    fontFamily: 'Cinzel', 
                    fontWeight: '700', 
                    fontSize: '16px',
                    color: '#3e2b26' 
                }}>
                    {right}
                </div>

                {/* Bottom Right Label (Value) */}
                {bottomRight && (
                    <div style={{ 
                        position: 'absolute', 
                        bottom: '50px', 
                        right: '0px', 
                        fontFamily: 'Cinzel', 
                        fontWeight: '700', 
                        fontSize: '14px',
                        color: '#3e2b26' 
                    }}>
                        {bottomRight}
                    </div>
                )}

                <svg width="200" height="200" viewBox="0 0 200 200" style={{ overflow: 'visible' }}>
                    <VintageGoldDefs />

                    {/* 
                        Segments Logic:
                        0 degrees is 12 o'clock (Top).
                        Segment 1 (Right): 0 to 120
                        Segment 2 (Bottom): 120 to 240
                        Segment 3 (Left): 240 to 360 (0)
                    */}
                    
                    {/* Segment 1: Right (Top-Right) */}
                    <Segment startAngle={0} endAngle={120} label="39%" />
                    
                    {/* Segment 2: Bottom */}
                    <Segment startAngle={120} endAngle={240} label="39%" />
                    
                    {/* Segment 3: Left (Top-Left) */}
                    <Segment startAngle={240} endAngle={360} label="39%" />

                    {/* Center Circle Background - Dark Metal */}
                    <circle cx="100" cy="100" r="48" fill="url(#darkMetalGradient)" stroke="#8c6a3e" strokeWidth="2" filter="url(#goldTexture)" />
                    
                    {/* Center Text - Gold Embossed */}
                    <text 
                        x="100" 
                        y="105" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill="#f4e4bc" 
                        fontFamily="Cinzel" 
                        fontSize="24" 
                        fontWeight="700" 
                        filter="url(#textEmboss)"
                    >
                        {displayValue}
                    </text>
                </svg>
            </div>

            <div className="bhima-card-subtext" style={{ 
                marginTop: '-15px', 
                width: '90%', 
                borderTop: 'none', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '8px',
                fontSize: '12px'
            }}>
                 <span style={{ color: '#3e2b26', fontWeight: 'bold', fontFamily: 'Cinzel' }}>• Current</span>
                 <div style={{ 
                     width: '12px', 
                     height: '12px', 
                     borderRadius: '50%', 
                     background: 'radial-gradient(circle at 30% 30%, #f4e4bc, #9e8053)', 
                     border: '1px solid #5c442d',
                     boxShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                 }}></div>
                 <span style={{ color: '#6b5a52', fontStyle: 'italic', fontFamily: 'Playfair Display' }}>| {displaySubtext}</span>
            </div>
        </div>
    );
};

export default BhimaGoldChart;
